import React, { useEffect, useMemo, useState } from 'react';
import { useSet } from 'react-use';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import { createPortal } from 'react-dom';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Card } from '../../../../../CommonComponents/OPL/Card/Card';
import { Dot } from '../../../../../CommonComponents/OPL/Dot/Dot';
import { Cell } from '../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { SegmentedControl } from '../../../../../CommonComponents/OPL/SegmentedControl/SegmentedControl';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Wrapper } from '../../../../../CommonComponents/OPL/Wrapper/Wrapper';
import { SERIES_COLORS } from '../../../../../CommonComponents/OPL/charts/charts.constants';
import { PerformanceTableRow } from '../../../../components/PerformanceDetails/PerformanceDetails.styles';
import { usePositionsDate } from '../../../../hooks/usePositionsDate';
import { usePositionsDetails } from '../../../../hooks/usePositionsDetails';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { ClickablePosition } from '../ClickablePosition/ClickablePosition';
import { PositionsModal } from '../PositionsModal/PositionsModal';
import { PositionsTableEmpty } from './PositionsTable.empty';
import { PositionsTableError } from './PositionsTable.error';
import { PositionsTableLoading } from './PositionsTable.loading';
import { StyledHtmlTable } from './PositionsTable.styles';
import { Separator } from '../../../../../CommonComponents/OPL/Separator/Separator';
import { Sorting, SortingDirection } from '../AlternativeDetail/AlternativeDetail.meta';
import { RawButton } from '../../../../../CommonComponents/OPL/Button/Button.styles';
import { sortTableRows } from '../../../../helpers/sortTableRows';
import { PercentageChart } from '../../../../components/PercentageChart/PercentageChart';
import { ChartType } from '../../../../../CommonSelectors/CommonArea.types';
import { selectOrionWidgetsChartType } from '../../../../../CommonSelectors/CommonAreaSelector';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { useFilteredRows } from './hooks/useFilteredRows';
import { PositionsDetailsCsvExport } from '../../../../components/PositionsDetailsCsvExport/PositionsDetailsCsvExport';
import { useExportCsvButtonContainer } from '../../../../hooks/useExportCsvButtonContainer';

export const PositionsTable = ({ isEmbeddedMobile }) => {
  const { accountId, clientId, groupId, allocationType } = useRouteParams();

  const date = usePositionsDate();

  const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);

  const [positionName, setPositionName] = useState<string>();

  const [, { has: isRowExpanded, toggle: toggleRow }] = useSet<string>();

  const { isFetching, isError, refetch, data } = usePositionsDetails({
    clientId,
    date,
    accountId,
    groupId,
    allocationType,
    selectedTabIndex,
  });

  const chartType = useSelector(selectOrionWidgetsChartType);

  const [sorting, setSorting] = useState<Sorting>({});

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  useEffect(() => {
    setSearchTerm('');
  }, [selectedTabIndex]);

  const unfilteredRows = useMemo(() => {
    if (!data?.tableData?.rows) {
      return null;
    }

    if (Object.keys(sorting).length === 0) {
      return data.tableData.rows;
    }

    const sortingKey = Object.keys(sorting)[0];

    return sortTableRows(data.tableData.rows, sortingKey, sorting[sortingKey]);
  }, [data, sorting]);

  const rows = useFilteredRows({ rows: unfilteredRows, filter: debouncedSearchTerm });

  const exportCsvButtonContainer = useExportCsvButtonContainer();

  if (isFetching) {
    return <PositionsTableLoading isEmbeddedMobile={isEmbeddedMobile} />;
  }

  if (isError) {
    return <PositionsTableError isEmbeddedMobile={isEmbeddedMobile} onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  const { tabList, tableData, chartData, tabIndex, showCostBasis } = data;

  if (tabList.length === 0) {
    return <PositionsTableEmpty isEmbeddedMobile={isEmbeddedMobile} />;
  }

  if (!tableData || !chartData) {
    return null;
  }

  const TableWrapper = isEmbeddedMobile ? Box : Card;

  return (
    <>
      {!!exportCsvButtonContainer &&
        createPortal(
          <PositionsDetailsCsvExport selectedGroupName={tabList[selectedTabIndex ?? tabIndex]} />,
          exportCsvButtonContainer,
        )}

      <Wrapper>
        <Box>
          {tabList.length > 1 && isEmbeddedMobile ? (
            <Select
              value={{
                label: tabList[selectedTabIndex ?? tabIndex],
                value: selectedTabIndex ?? tabIndex,
              }}
              onChange={opt => setSelectedTabIndex(opt?.value ?? tabIndex)}
              options={tabList.map((label, i) => ({ label, value: i }))}
              mx="16"
              isSearchable={false}
            />
          ) : (
            <SegmentedControl
              id="positions-sc"
              values={tabList}
              selectedIndex={selectedTabIndex ?? tabIndex}
              onChange={setSelectedTabIndex}
              mt="16"
            />
          )}
        </Box>

        {isEmbeddedMobile && <Separator mt="16" />}

        <TableWrapper mt={isEmbeddedMobile ? undefined : '16'}>
          {!isEmbeddedMobile && (
            <Text variant="cardTitle" px="16" pt="12" pb="4">
              Allocation
            </Text>
          )}

          <Box px="16" pt={isEmbeddedMobile ? '16' : '12'} pb={isEmbeddedMobile ? '16' : '24'}>
            <PercentageChart
              data={chartData}
              size={256}
              chartType={isEmbeddedMobile ? ChartType.Bar : chartType}
            />
          </Box>

          {!isEmbeddedMobile && (
            <Box p="16">
              <Input
                value={searchTerm}
                onChange={({ target: { value } }) => setSearchTerm(value)}
                placeholder="Search..."
                leftIcon="fa fa-search"
                aria-label="Search"
              />
            </Box>
          )}

          <StyledHtmlTable
            variant="embedded"
            columnSpacing={tableData.headers.length <= 5 ? 'large' : 'medium'}
            isEmbeddedMobile={isEmbeddedMobile}
          >
            <table>
              <colgroup>
                <col width="100%" />
                <col width="0%" />
              </colgroup>

              <thead>
                <tr>
                  {tableData.headers.map((header, index) => (
                    <th key={header.text}>
                      <RawButton
                        onClick={() => {
                          if (sorting[index]) {
                            setSorting(
                              sorting[index] === SortingDirection.Asc
                                ? { [index]: SortingDirection.Desc }
                                : {},
                            );
                          } else {
                            setSorting({ [index]: SortingDirection.Asc });
                          }
                        }}
                        aria-label="Sort by column"
                        css="width: 100%"
                      >
                        <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                          <Text
                            variant="tableHeader"
                            textAlign={index === 0 ? 'left' : 'right'}
                            width="100%"
                          >
                            {header.text}
                          </Text>
                          {(sorting[index] === SortingDirection.Asc ||
                            sorting[index] === SortingDirection.Desc) && (
                            <Icon
                              className={
                                sorting[index] === SortingDirection.Asc
                                  ? 'fas fa-arrow-up'
                                  : 'fas fa-arrow-down'
                              }
                              color="neutral.500"
                            />
                          )}
                        </Box>
                      </RawButton>
                    </th>
                  ))}

                  {isEmbeddedMobile && (
                    <th>
                      <Text variant="tableHeader" textAlign="right" width="100%">
                        Amount
                      </Text>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {(rows ?? []).map(({ id, cells, childRows, isTotal, color }, rowIndex) => {
                  const hasChildRows = childRows && childRows.length > 0;
                  const isExpanded = isRowExpanded(id) || (!!searchTerm && hasChildRows);
                  const tableRowVariant = hasChildRows ? 'clickable' : 'default';

                  return (
                    <React.Fragment key={id}>
                      <PerformanceTableRow
                        isExpanded={isExpanded}
                        onClick={hasChildRows && !searchTerm ? () => toggleRow(id) : undefined}
                        variant={tableRowVariant}
                      >
                        {cells.map((cell, index) => (
                          <Cell key={index} isFlex={index === 0}>
                            <Box display="flex" alignItems="center">
                              {index === 0 && hasChildRows && !searchTerm && (
                                <Icon
                                  className={
                                    isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'
                                  }
                                  color="neutral.400"
                                  mr={isExpanded ? 8 : 12}
                                />
                              )}

                              {index === 0 ? (
                                <>
                                  {!isTotal && (
                                    <Dot
                                      bg={color || SERIES_COLORS[rowIndex % SERIES_COLORS.length]}
                                      mr="8"
                                    />
                                  )}

                                  <TruncatedText
                                    fontWeight={600}
                                    color="neutral.700"
                                    textAlign="left"
                                    width="100%"
                                  >
                                    {cell.text}
                                  </TruncatedText>
                                </>
                              ) : (
                                <Text
                                  fontWeight={600}
                                  color="neutral.700"
                                  textAlign="right"
                                  width="100%"
                                >
                                  {cell.text}
                                </Text>
                              )}
                            </Box>
                          </Cell>
                        ))}

                        {isEmbeddedMobile && (
                          <Cell isFlex={false}>
                            <Text
                              fontWeight={600}
                              color="neutral.600"
                              textAlign="right"
                              width="100%"
                            >
                              {cells[1].text}
                            </Text>
                            <Text
                              fontSize="small"
                              fontWeight={600}
                              color="neutral.500"
                              textAlign="right"
                              width="100%"
                            >
                              {cells[cells.length - 1].text}
                            </Text>
                          </Cell>
                        )}
                      </PerformanceTableRow>

                      {isExpanded &&
                        childRows &&
                        childRows.map((childRow, key) => (
                          <PerformanceTableRow key={key} variant="default">
                            {childRow.cells.map((childCell, index) => (
                              <Cell key={index} isFlex={index === 0}>
                                {index === 0 ? (
                                  <Box>
                                    {showCostBasis && !isEmbeddedMobile ? (
                                      <ClickablePosition
                                        onClick={e => {
                                          e.currentTarget.blur();
                                          setPositionName(childCell.subText);
                                        }}
                                      >
                                        {childCell.text}
                                      </ClickablePosition>
                                    ) : (
                                      <TruncatedText textAlign="left" width="100%" pl="40">
                                        {childCell.text}
                                      </TruncatedText>
                                    )}

                                    {childCell.subText && (
                                      <TruncatedText
                                        color="neutral.500"
                                        fontSize="small"
                                        textAlign="left"
                                        width="100%"
                                        pl="40"
                                      >
                                        {childCell.subText}
                                      </TruncatedText>
                                    )}
                                  </Box>
                                ) : (
                                  <Text color="neutral.700" textAlign="right" width="100%">
                                    {childCell.text}
                                  </Text>
                                )}
                              </Cell>
                            ))}

                            {isEmbeddedMobile && (
                              <Cell isFlex={false}>
                                <Text
                                  fontWeight={500}
                                  color="neutral.600"
                                  textAlign="right"
                                  width="100%"
                                >
                                  {childRow.cells[1].text}
                                </Text>
                                <Text
                                  fontSize="small"
                                  fontWeight={500}
                                  color="neutral.500"
                                  textAlign="right"
                                  width="100%"
                                >
                                  {childRow.cells[childRow.cells.length - 1].text}
                                </Text>
                              </Cell>
                            )}
                          </PerformanceTableRow>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </StyledHtmlTable>
        </TableWrapper>

        {positionName && (
          <PositionsModal
            positionName={positionName}
            onRequestClose={() => setPositionName(undefined)}
          />
        )}
      </Wrapper>
    </>
  );
};
